<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { RouterLink } from 'vue-router'
import { onBeforeRouteLeave, useRoute, useRouter } from 'vue-router'
import AdminActionDialog from '../components/AdminActionDialog.vue'
import LifecycleStatusBadge from '../components/LifecycleStatusBadge.vue'
import LotterySkeletonCard from '../components/LotterySkeletonCard.vue'
import { useLotteryStore } from '../stores/lottery.js'
import { useAuthStore } from '../stores/auth.js'
import { useOrgStore } from '../stores/orgStore.js'
import { usePermissions } from '../utils/permissions.js'
import { awaitScopedContextReady } from '../utils/contextReady.js'
import { parseAdminError, toAdminLoadingMessage } from '../utils/adminUi.js'
import { useDebouncedValue } from '../utils/debounce.js'
import { DEFAULT_ADMIN_PAGE_SIZE, DEFAULT_ADMIN_SEARCH_DEBOUNCE_MS, isDataStale, paginateRows, toPositiveInt } from '../utils/operationsUi.js'
import { downloadCsv, getRecentExportHistory, recordExportEvent, toCsv, toLifecycleFilename } from '../utils/exportUi.js'
import { useQueryState } from '../composables/useQueryState.js'
import {
  canArchiveLottery,
  canCloseLottery,
  canDrawLottery,
  canEditLottery,
  canPauseLottery,
  canResumeLottery,
  getLotteryLifecycleMeta,
  normalizeLotteryStatus,
  parseDrawExecutionError,
  toLotteryAuditMessage,
} from '../utils/lotteryUi.js'

const lotteryStore = useLotteryStore()
const authStore = useAuthStore()
const orgStore = useOrgStore()
const route = useRoute()
const router = useRouter()
const { can } = usePermissions(authStore, orgStore)

const listLoading = ref(false)
const transitioningLotteryId = ref(null)
const pendingActionLoading = ref(false)
const operationMessage = ref('')
const operationError = ref('')
const statusFilter = ref('all')
const searchTerm = ref('')
const debouncedSearchTerm = useDebouncedValue(searchTerm, DEFAULT_ADMIN_SEARCH_DEBOUNCE_MS)
const listPage = ref(1)
const listPageSize = ref(DEFAULT_ADMIN_PAGE_SIZE)
const pendingAction = ref(null)
const drawResult = ref(null)
const drawTransitionActive = ref(false)
const lastSuccessfulRefreshAt = ref('')
const degradedWarning = ref('')
const exportDialogOpen = ref(false)
const exportLoading = ref(false)
const exportError = ref('')
const exportScope = ref('execution')
const exportHistory = ref([])
let beforeUnloadHandler = null

const lotteries = computed(() => lotteryStore.lotteries ?? [])

const filteredLotteries = computed(() => {
  const query = String(debouncedSearchTerm.value || '').trim().toLowerCase()

  return lotteries.value.filter((lottery) => {
    const status = normalizeLotteryStatus(lottery)

    if (statusFilter.value !== 'all' && status !== statusFilter.value) {
      return false
    }

    if (!query) return true

    const idLabel = String(getLotteryId(lottery) || '')
    const nameLabel = String(lottery?.name ?? '').toLowerCase()
    return nameLabel.includes(query) || idLabel.includes(query)
  })
})
const readOnlyMode = computed(() => !can.createLottery.value && !authStore.isSuperAdmin)
const hasItems = computed(() => filteredLotteries.value.length > 0)
const staleData = computed(() => isDataStale(lastSuccessfulRefreshAt.value, 120000))
const paginatedLotteries = computed(() => paginateRows(filteredLotteries.value, listPage.value, listPageSize.value))
const pageRows = computed(() => paginatedLotteries.value.items)
const totalPages = computed(() => paginatedLotteries.value.totalPages)
const hasPreviousPage = computed(() => Number(listPage.value) > 1)
const hasNextPage = computed(() => Number(listPage.value) < Number(totalPages.value))
const queryState = useQueryState({
  route,
  router,
  entries: [
    { queryKey: 'page', type: 'int', defaultValue: 1, get: () => listPage.value, set: (value) => { listPage.value = toPositiveInt(value, 1) } },
    { queryKey: 'page_size', type: 'int', defaultValue: DEFAULT_ADMIN_PAGE_SIZE, get: () => listPageSize.value, set: (value) => { listPageSize.value = toPositiveInt(value, DEFAULT_ADMIN_PAGE_SIZE) } },
    { queryKey: 'q', defaultValue: '', get: () => searchTerm.value, set: (value) => { searchTerm.value = String(value || '') } },
    { queryKey: 'status', defaultValue: 'all', get: () => statusFilter.value, set: (value) => { statusFilter.value = String(value || 'all') } },
  ],
})

const syncQueryFromState = async () => {
  await queryState.syncToRoute()
}

const hydrateStateFromQuery = () => {
  queryState.hydrateFromQuery()
}

const clearFeedback = () => {
  operationMessage.value = ''
  operationError.value = ''
}

const getLotteryId = (lottery) => Number(lottery?.id ?? lottery?.lottery_id)
const getTicketPrice = (lottery) => {
  const value = lottery?.ticket_price ?? lottery?.ticketPrice
  if (value === null || value === undefined || value === '') return '-'
  return String(value)
}
const getIsLocked = (lottery) => (lottery?.is_locked === true ? 'Yes' : 'No')

const parseLotteryError = (err, fallback) => {
  return parseAdminError(err, fallback, {
    notFound: 'This lottery is no longer available. Refresh and try again.',
    forbidden: 'You no longer have permission to perform this lottery action.',
    conflict: 'Lottery lifecycle changed on the server. Refresh and try again.',
  })
}

const loadLotteries = async ({ preserveFeedback = false } = {}) => {
  if (!preserveFeedback) {
    clearFeedback()
  }

  const ready = await awaitScopedContextReady(authStore, orgStore)
  if (!ready) return

  listLoading.value = true
  degradedWarning.value = ''
  try {
    const result = await lotteryStore.fetchLotteries()
    if (!result?.success && result?.error) {
      operationError.value = parseLotteryError(result.error, 'Failed to load lotteries.')
      degradedWarning.value = 'Lottery backend responded with an operational error. Retry recommended.'
      return
    }
    lastSuccessfulRefreshAt.value = new Date().toISOString()
    exportHistory.value = getRecentExportHistory()
  } finally {
    listLoading.value = false
  }
}

const canArchiveRow = (lottery) => canArchiveLottery(lottery) || authStore.isSuperAdmin
const canModifyRow = computed(() => can.createLottery.value || authStore.isSuperAdmin)

const isRowBusy = (lottery) => transitioningLotteryId.value === getLotteryId(lottery)
const isDrawLocked = (lottery) => lotteryStore.isDrawLocked(getLotteryId(lottery))
const isAnyDrawActive = computed(() => drawTransitionActive.value || Object.values(lotteryStore.drawLocks || {}).some(Boolean))

const preventUnloadWhileDrawing = (event) => {
  if (!isAnyDrawActive.value) return
  event.preventDefault()
  event.returnValue = 'Draw execution is still in progress.'
}

onBeforeRouteLeave(() => {
  if (!isAnyDrawActive.value) return true
  operationError.value = 'Draw execution is still in progress. Navigation is temporarily blocked until completion.'
  return false
})

watch(isAnyDrawActive, (active) => {
  if (typeof window === 'undefined') return

  if (active && !beforeUnloadHandler) {
    beforeUnloadHandler = preventUnloadWhileDrawing
    window.addEventListener('beforeunload', beforeUnloadHandler)
    return
  }

  if (!active && beforeUnloadHandler) {
    window.removeEventListener('beforeunload', beforeUnloadHandler)
    beforeUnloadHandler = null
  }
})

const resolveLatestExecutionTimestamp = (lottery) => {
  return lottery?.last_executed_at
    ?? lottery?.executed_at
    ?? lottery?.latest_execution?.executed_at
    ?? lottery?.latest_draw_result?.executed_at
    ?? null
}

const resolveExecutionStatus = (lottery) => {
  const status = String(lottery?.execution_status ?? lottery?.latest_execution?.execution_status ?? '').trim().toLowerCase()
  if (status) return status

  const lifecycle = normalizeLotteryStatus(lottery)
  if (lifecycle === 'archived') return 'archived'
  if (lifecycle === 'drawn') return 'success'
  return resolveLatestExecutionTimestamp(lottery) ? 'recoverable' : 'never'
}

const toExecutionSummary = (lottery) => {
  if (isDrawLocked(lottery)) return 'Draw in progress (locked)'
  const status = resolveExecutionStatus(lottery)
  if (status === 'never') return 'Never drawn'
  if (status === 'success' || status === 'drawn') return 'Drawn successfully'
  if (status === 'archived') return 'Archived'
  if (status === 'failed') return 'Draw failed (recoverable)'
  if (status === 'recoverable' || status === 'partial') return 'Partial/recoverable result'
  return 'Execution status unavailable'
}

const executionClass = (lottery) => {
  if (isDrawLocked(lottery)) return 'exec-running'
  const status = resolveExecutionStatus(lottery)
  if (status === 'never') return 'exec-never'
  if (status === 'success' || status === 'drawn') return 'exec-success'
  if (status === 'archived') return 'exec-archived'
  return 'exec-recoverable'
}

const openActionDialog = (type, lottery) => {
  if (!lottery || readOnlyMode.value || isRowBusy(lottery)) return
  if (type === 'draw' && isDrawLocked(lottery)) return

  clearFeedback()
  pendingAction.value = {
    type,
    lottery,
    step: type === 'draw' ? 1 : null,
  }
}

const changePage = async (delta) => {
  const next = Number(listPage.value) + Number(delta)
  listPage.value = Math.max(1, Math.min(Number(totalPages.value), next))
  await syncQueryFromState()
}

const updatePageSize = async (nextPageSize) => {
  listPageSize.value = toPositiveInt(nextPageSize, DEFAULT_ADMIN_PAGE_SIZE)
  listPage.value = 1
  await syncQueryFromState()
}

const openExportDialog = () => {
  exportError.value = ''
  exportDialogOpen.value = true
}

const closeExportDialog = () => {
  if (exportLoading.value) return
  exportDialogOpen.value = false
}

const exportAdminCsv = async () => {
  if (!filteredLotteries.value.length || exportLoading.value) return
  exportLoading.value = true
  exportError.value = ''
  try {
    const rows = filteredLotteries.value.map((lottery) => ({
      id: getLotteryId(lottery),
      name: lottery?.name || '',
      ticket_price: getTicketPrice(lottery),
      locked: getIsLocked(lottery),
      lifecycle: normalizeLotteryStatus(lottery),
      execution_status: resolveExecutionStatus(lottery),
      latest_execution: resolveLatestExecutionTimestamp(lottery) || '',
    }))

    const csv = toCsv(rows, [
      { key: 'id', label: 'id' },
      { key: 'name', label: 'name' },
      { key: 'ticket_price', label: 'ticket_price' },
      { key: 'locked', label: 'locked' },
      { key: 'lifecycle', label: 'lifecycle' },
      { key: 'execution_status', label: 'execution_status' },
      { key: 'latest_execution', label: 'latest_execution' },
    ])

    const filename = toLifecycleFilename({
      scope: exportScope.value === 'winners' ? 'winner-report' : 'lottery-execution',
      lifecycle: statusFilter.value,
    })
    downloadCsv(filename, csv)
    recordExportEvent({
      exported_by: authStore.user?.username || authStore.user?.email || 'unknown',
      export_scope: exportScope.value,
      partial_export_warning: Number(rows.length) < Number(lotteries.value.length),
      record_count: rows.length,
    })
    exportHistory.value = getRecentExportHistory()
    exportDialogOpen.value = false
  } catch {
    exportError.value = 'Failed to export lottery operational report.'
  } finally {
    exportLoading.value = false
  }
}

const closeActionDialog = () => {
  if (pendingActionLoading.value) return
  pendingAction.value = null
}

const getActionTitle = computed(() => {
  const type = pendingAction.value?.type
  if (type === 'pause') return 'Pause Lottery'
  if (type === 'resume') return 'Resume Lottery'
  if (type === 'close') return 'Close Lottery'
  if (type === 'archive') return 'Archive Lottery'
  if (type === 'draw' && pendingAction.value?.step === 1) return 'Confirm Draw Execution'
  if (type === 'draw' && pendingAction.value?.step === 2) return 'Final Confirmation: Execute Draw'
  return 'Confirm Lottery Action'
})

const getActionMessage = computed(() => {
  const lotteryName = pendingAction.value?.lottery?.name || 'This lottery'
  const type = pendingAction.value?.type

  if (type === 'pause') return `${lotteryName} will be paused and ticket operations will stop until resumed.`
  if (type === 'resume') return `${lotteryName} will be resumed and return to active operation.`
  if (type === 'close') return `${lotteryName} will be closed and no further submissions will be accepted.`
  if (type === 'archive') return `${lotteryName} will be archived and become read-only.`
  if (type === 'draw' && pendingAction.value?.step === 1) return `${lotteryName} draw is irreversible. Confirm you want to prepare winner execution.`
  if (type === 'draw' && pendingAction.value?.step === 2) return `${lotteryName} winners will now be drawn and audit-logged. This operation cannot be undone.`
  return 'Confirm action.'
})

const getActionDetails = computed(() => {
  const type = pendingAction.value?.type

  if (type === 'draw') {
    return [
      'Draw execution is single-run guarded.',
      'Result metadata and winners are persisted for audit history.',
    ]
  }

  if (type === 'archive') {
    return ['Archived lotteries remain visible for history and auditability.']
  }

  return ['This operation is tracked in audit logs.']
})

const getActionTone = computed(() => {
  const type = pendingAction.value?.type
  if (type === 'resume') return 'success'
  if (type === 'pause' || type === 'close') return 'warning'
  return 'danger'
})

const getActionConfirmLabel = computed(() => {
  const type = pendingAction.value?.type
  if (type === 'pause') return 'Pause Lottery'
  if (type === 'resume') return 'Resume Lottery'
  if (type === 'close') return 'Close Lottery'
  if (type === 'archive') return 'Archive Lottery'
  if (type === 'draw' && pendingAction.value?.step === 1) return 'Continue'
  if (type === 'draw' && pendingAction.value?.step === 2) return 'Execute Draw'
  return 'Confirm'
})

const performLifecycleAction = async (type, lottery) => {
  const lotteryId = getLotteryId(lottery)
  if (!Number.isInteger(lotteryId) || lotteryId <= 0) return

  transitioningLotteryId.value = lotteryId

  try {
    if (type === 'pause') {
      const result = await lotteryStore.pauseLottery(lotteryId)
      if (!result.success) {
        operationError.value = parseLotteryError(result.error, 'Failed to pause lottery.')
        return
      }
      operationMessage.value = toLotteryAuditMessage('LOTTERY_PAUSED', lottery.name || 'Lottery')
    }

    if (type === 'resume') {
      const result = await lotteryStore.resumeLottery(lotteryId)
      if (!result.success) {
        operationError.value = parseLotteryError(result.error, 'Failed to resume lottery.')
        return
      }
      operationMessage.value = toLotteryAuditMessage('LOTTERY_RESUMED', lottery.name || 'Lottery')
    }

    if (type === 'close') {
      const result = await lotteryStore.closeLottery(lotteryId)
      if (!result.success) {
        operationError.value = parseLotteryError(result.error, 'Failed to close lottery.')
        return
      }
      operationMessage.value = toLotteryAuditMessage('LOTTERY_CLOSED', lottery.name || 'Lottery')
    }

    if (type === 'archive') {
      const result = await lotteryStore.archiveLottery(lotteryId)
      if (!result.success) {
        operationError.value = parseLotteryError(result.error, 'Failed to archive lottery.')
        return
      }
      operationMessage.value = toLotteryAuditMessage('LOTTERY_ARCHIVED', lottery.name || 'Lottery')
    }

    if (type === 'draw') {
      drawTransitionActive.value = true
      const result = await lotteryStore.drawWinner(lotteryId)
      if (!result.success) {
        operationError.value = parseDrawExecutionError(result.error, parseLotteryError(result.error, 'Failed to execute draw.'))
        drawTransitionActive.value = false
        return
      }

      drawResult.value = {
        winners: Array.isArray(result.result?.winners) ? result.result.winners : [],
        executedAt: result.result?.executed_at ?? new Date().toISOString(),
        metadata: {
          tickets: Number(result.result?.tickets_count ?? result.result?.metadata?.tickets_count ?? 0),
          participants: Number(result.result?.participants_count ?? result.result?.metadata?.participants_count ?? 0),
        },
      }
      operationMessage.value = toLotteryAuditMessage('LOTTERY_DRAW_EXECUTED', lottery.name || 'Lottery')
      drawTransitionActive.value = false
    }

    await loadLotteries({ preserveFeedback: true })
  } finally {
    drawTransitionActive.value = false
    transitioningLotteryId.value = null
  }
}

const confirmPendingAction = async () => {
  if (!pendingAction.value || pendingActionLoading.value) return

  if (pendingAction.value.type === 'draw' && pendingAction.value.step === 1) {
    pendingAction.value = {
      ...pendingAction.value,
      step: 2,
    }
    return
  }

  pendingActionLoading.value = true
  try {
    await performLifecycleAction(pendingAction.value.type, pendingAction.value.lottery)
    pendingAction.value = null
  } finally {
    pendingActionLoading.value = false
  }
}

watch(
  () => [orgStore.activeOrgId, orgStore.activeSubOrgId],
  async ([nextOrgId, nextSubOrgId], [previousOrgId, previousSubOrgId]) => {
    if (nextOrgId === previousOrgId && nextSubOrgId === previousSubOrgId) return
    pendingAction.value = null
    transitioningLotteryId.value = null
    await loadLotteries()
  },
)

watch(
  () => route.fullPath,
  () => {
    pendingAction.value = null
    pendingActionLoading.value = false
    transitioningLotteryId.value = null
  },
)

watch([debouncedSearchTerm, statusFilter], async () => {
  listPage.value = 1
  await syncQueryFromState()
})

onMounted(async () => {
  hydrateStateFromQuery()
  await loadLotteries()
  await syncQueryFromState()
})

onBeforeUnmount(() => {
  if (beforeUnloadHandler) {
    window.removeEventListener('beforeunload', beforeUnloadHandler)
    beforeUnloadHandler = null
  }
})
</script>

<template>
  <section class="lottery-admin-view">
    <header class="header-row">
      <h1>Lottery Admin</h1>
      <div class="actions">
        <button class="btn" type="button" :disabled="listLoading" @click="loadLotteries()">
          {{ listLoading ? toAdminLoadingMessage('lotteries') : 'Refresh' }}
        </button>
        <button class="btn btn-secondary" type="button" :disabled="listLoading || !filteredLotteries.length" @click="openExportDialog">
          Export CSV
        </button>
      </div>
    </header>

    <p v-if="operationMessage" class="success">{{ operationMessage }}</p>
    <p v-if="operationError || lotteryStore.error" class="error">{{ operationError || lotteryStore.error }}</p>
    <p v-if="degradedWarning" class="warning">{{ degradedWarning }}</p>
    <p v-if="staleData && !listLoading" class="warning">Lottery data may be stale. Refresh recommended.</p>
    <p v-if="lastSuccessfulRefreshAt" class="muted">Last successful refresh: {{ lastSuccessfulRefreshAt }}</p>

    <div class="panel filters-row">
      <select v-model="statusFilter" class="form-select" data-testid="lottery-status-filter">
        <option value="all">All statuses</option>
        <option value="active">Active</option>
        <option value="closed">Closed</option>
        <option value="drawn">Drawn</option>
        <option value="archived">Archived</option>
      </select>
      <input
        v-model="searchTerm"
        type="search"
        class="form-control"
        placeholder="Search by name or id"
        data-testid="lottery-search"
      />
      <select class="form-select" :value="String(listPageSize)" @change="updatePageSize($event.target.value)">
        <option value="25">25 / page</option>
        <option value="50">50 / page</option>
        <option value="100">100 / page</option>
      </select>
    </div>

    <div v-if="listLoading && !lotteries.length" class="skeleton-grid">
      <LotterySkeletonCard v-for="n in 3" :key="n" />
    </div>

    <div v-else-if="!hasItems" class="empty-state">No lotteries match the current filters.</div>

    <div v-else class="table-wrap">
      <table class="table lottery-table">
        <thead>
          <tr>
            <th>ID</th>
            <th>Name</th>
            <th>Ticket Price</th>
            <th>Locked</th>
            <th>Status</th>
            <th>Latest Execution</th>
            <th>Execution Outcome</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="lottery in pageRows" :key="getLotteryId(lottery)">
            <td>#{{ getLotteryId(lottery) }}</td>
            <td>{{ lottery.name || 'Unnamed lottery' }}</td>
            <td>{{ getTicketPrice(lottery) }}</td>
            <td>{{ getIsLocked(lottery) }}</td>
            <td>
              <LifecycleStatusBadge :status-meta="getLotteryLifecycleMeta(lottery)" />
            </td>
            <td>{{ resolveLatestExecutionTimestamp(lottery) || 'Never' }}</td>
            <td>
              <span class="execution-chip" :class="executionClass(lottery)">{{ toExecutionSummary(lottery) }}</span>
            </td>
            <td class="actions">
              <RouterLink class="btn btn-link" :to="`/lottery/${getLotteryId(lottery)}`">View details</RouterLink>
              <RouterLink class="btn btn-link" :to="`/lottery/${getLotteryId(lottery)}#execution-history`">Execution history</RouterLink>
              <RouterLink class="btn btn-link" :to="`/lottery/${getLotteryId(lottery)}#execution-integrity`">Integrity</RouterLink>
              <RouterLink class="btn btn-link" :to="`/lottery/${getLotteryId(lottery)}#draw-outcomes`">Outcomes</RouterLink>
              <button
                class="btn"
                type="button"
                :disabled="!canModifyRow || !canEditLottery(lottery) || isRowBusy(lottery)"
              >
                Edit
              </button>
              <button
                class="btn"
                type="button"
                :disabled="!canModifyRow || !canPauseLottery(lottery) || isRowBusy(lottery)"
                @click="openActionDialog('pause', lottery)"
              >
                {{ isRowBusy(lottery) ? 'Working...' : 'Pause' }}
              </button>
              <button
                class="btn"
                type="button"
                :disabled="!canModifyRow || !canResumeLottery(lottery) || isRowBusy(lottery)"
                @click="openActionDialog('resume', lottery)"
              >
                {{ isRowBusy(lottery) ? 'Working...' : 'Resume' }}
              </button>
              <button
                class="btn"
                type="button"
                :disabled="!canModifyRow || !canCloseLottery(lottery) || isRowBusy(lottery)"
                @click="openActionDialog('close', lottery)"
              >
                {{ isRowBusy(lottery) ? 'Working...' : 'Close' }}
              </button>
              <button
                class="btn btn-danger"
                type="button"
                :disabled="!canModifyRow || !canArchiveRow(lottery) || isRowBusy(lottery)"
                @click="openActionDialog('archive', lottery)"
              >
                {{ isRowBusy(lottery) ? 'Working...' : 'Archive' }}
              </button>
              <button
                class="btn btn-warning"
                type="button"
                :disabled="!can.drawWinner || !canDrawLottery(lottery) || isRowBusy(lottery) || isDrawLocked(lottery)"
                @click="openActionDialog('draw', lottery)"
              >
                {{ isRowBusy(lottery) || isDrawLocked(lottery) ? 'Drawing winners...' : 'Draw' }}
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <footer v-if="hasItems" class="pagination-row">
      <button class="btn btn-secondary" type="button" :disabled="listLoading || !hasPreviousPage" @click="changePage(-1)">Previous</button>
      <span>Page {{ listPage }} of {{ totalPages }} ({{ paginatedLotteries.total }} lotteries)</span>
      <button class="btn btn-secondary" type="button" :disabled="listLoading || !hasNextPage" @click="changePage(1)">Next</button>
    </footer>

    <section v-if="drawResult" class="panel draw-result">
      <h2>Draw Result</h2>
      <p><strong>Executed at:</strong> {{ drawResult.executedAt }}</p>
      <p><strong>Tickets:</strong> {{ drawResult.metadata.tickets }} | <strong>Participants:</strong> {{ drawResult.metadata.participants }}</p>
      <ul v-if="drawResult.winners.length">
        <li v-for="winner in drawResult.winners" :key="winner.id || winner.ticket_number || winner.username">
          {{ winner.username || winner.name || 'Winner' }}
          <span v-if="winner.ticket_number">(Ticket #{{ winner.ticket_number }})</span>
        </li>
      </ul>
      <p v-else class="muted">No winners were returned from backend.</p>
    </section>

    <section class="panel">
      <h2>Recent Exports</h2>
      <ul v-if="exportHistory.length" class="muted-list">
        <li v-for="entry in exportHistory" :key="entry.id">
          {{ entry.export_scope }} - {{ entry.record_count }} rows - {{ entry.exported_at }}
          <span v-if="entry.partial_export_warning">(partial warning)</span>
        </li>
      </ul>
      <p v-else class="muted">No export history yet.</p>
    </section>

    <AdminActionDialog
      :open="exportDialogOpen"
      title="Export Lottery Report"
      message="Export current operational lottery list as CSV."
      :details="[
        'Exports capture current filter scope.',
        filteredLotteries.length < lotteries.length ? 'Export may be partial due to filters.' : 'Export contains full filtered result set.',
      ]"
      tone="warning"
      confirm-label="Export CSV"
      :loading="exportLoading"
      loading-label="Preparing export..."
      @close="closeExportDialog"
      @confirm="exportAdminCsv"
    />

    <p v-if="exportError" class="error">{{ exportError }}</p>

    <AdminActionDialog
      :open="Boolean(pendingAction)"
      :title="getActionTitle"
      :message="getActionMessage"
      :details="getActionDetails"
      :confirm-label="getActionConfirmLabel"
      :loading-label="pendingAction?.type === 'draw' ? 'Drawing winners...' : 'Applying transition...'"
      :tone="getActionTone"
      :loading="pendingActionLoading"
      @close="closeActionDialog"
      @confirm="confirmPendingAction"
    />
  </section>
</template>

<style scoped>
.lottery-admin-view {
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
  display: grid;
  gap: 1rem;
}

.header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
}

.filters-row {
  display: grid;
  gap: 0.5rem;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
}


.table-wrap {
  overflow-x: auto;
}

.lottery-table {
  width: 100%;
  border-collapse: collapse;
  background: #fff;
}

.lottery-table th,
.lottery-table td {
  border: 1px solid #e5e7eb;
  padding: 0.55rem;
  text-align: left;
}

.actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
}

.execution-chip {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 0.2rem 0.55rem;
  font-size: 0.82rem;
  border: 1px solid transparent;
}

.exec-never {
  color: #6b7280;
  border-color: #d1d5db;
  background: #f9fafb;
}

.exec-success {
  color: #166534;
  border-color: #86efac;
  background: #dcfce7;
}

.exec-running {
  color: #1d4ed8;
  border-color: #93c5fd;
  background: #eff6ff;
}

.exec-recoverable {
  color: #92400e;
  border-color: #fcd34d;
  background: #fffbeb;
}

.exec-archived {
  color: #1f2937;
  border-color: #9ca3af;
  background: #f3f4f6;
}

.btn {
  border: none;
  border-radius: 6px;
  padding: 0.4rem 0.65rem;
  background: #2563eb;
  color: #fff;
}

.btn-link {
  display: inline-flex;
  text-decoration: none;
  align-items: center;
}

.btn-danger {
  background: #b91c1c;
}

.btn-secondary {
  background: #4b5563;
}

.btn-warning {
  background: #92400e;
}

.success {
  color: #166534;
}

.error {
  color: #b91c1c;
}

.warning {
  color: #92400e;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 6px;
  padding: 0.45rem;
}

.empty-state,
.muted {
  color: #6b7280;
}

.muted-list {
  margin: 0;
  padding-left: 1rem;
  color: #6b7280;
}

.pagination-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}

.skeleton-grid {
  display: grid;
  gap: 0.75rem;
}

@media (max-width: 768px) {
  .actions {
    display: grid;
    grid-template-columns: 1fr 1fr;
  }
}
</style>









